import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowRight, Building2, Globe, Lock, Mail, Star, UserRound } from 'lucide-react';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Button } from '@/components/ui/Button';
import { TermsModal } from '@/components/common/TermsModal';
import { TextField } from '@/components/forms/TextField';
import { CheckboxField } from '@/components/forms/CheckboxField';
import { AuthHero } from '@/components/common/AuthHero';
import { IssueReportJourneySteps } from '@/components/common/IssueReportJourneySteps';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { useSignup } from '@/hooks/useSignup';
import { usePurchaseIntent } from '@/hooks/usePurchaseIntent';
import { ROUTES } from '@/lib/constants/routes';
import { withPurchaseIntent } from '@/lib/utils/purchase-intent.utils';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { mapApiError } from '@/lib/utils/error.utils';
import { requiredUrlSchema } from '@/lib/utils/url-schema.utils';
import { JOURNEY_ROUTE } from '@/lib/constants/issue-report';

// Full name, email and password are the fields the backend signup contract
// accepts (auth stays in the Backend, FE hard-rule #1). LinkedIn was dropped
// from it (A-50, FR-09) and the API now rejects the field, so the form must
// not send it. On a normal signup Company Name is an optional hint, validated
// client-side but not sent. On the Issue Report signup, company name and
// website are required and sent: they prefill the journey's startup step (FR-218).
const websiteSchema = requiredUrlSchema(UI_STRINGS.SIGNUP.COMPANY_WEBSITE_REQUIRED);

const signupFields = z
  .object({
    fullName: z.string().trim().min(1, 'Full name is required'),
    email: z
      .string()
      .trim()
      .min(1, 'Email is required')
      .email('Please enter a valid email address'),
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
    companyName: z.string().trim().max(200).optional(),
    companyWebsite: z.string().optional(),
    // Unchecked (RHF reports an unchecked native checkbox as `false`, never
    // `undefined`) or stripped from the submitted values entirely both fail
    // the same way - z.literal(true) only accepts the literal `true`.
    tosAccepted: z.literal(true),
  });

/** The signup schema; with the Issue Report intent the company fields are required too. */
function signupSchemaFor(issueReport: boolean) {
  return signupFields.superRefine((values, ctx) => {
    if (values.password !== values.confirmPassword) {
      ctx.addIssue({ code: 'custom', message: 'Passwords do not match', path: ['confirmPassword'] });
    }
    if (!issueReport) return;
    if (!values.companyName) {
      ctx.addIssue({ code: 'custom', message: UI_STRINGS.SIGNUP.COMPANY_NAME_REQUIRED, path: ['companyName'] });
    }
    const website = websiteSchema.safeParse(values.companyWebsite ?? '');
    if (!website.success) {
      ctx.addIssue({ code: 'custom', message: website.error.issues[0].message, path: ['companyWebsite'] });
    }
  });
}

type SignupFormValues = z.infer<typeof signupFields>;

/** The words that differ on the Issue Report signup (approved journey screens), resolved once per mode. */
interface SignupCopy {
  badge?: string;
  headingPrefix: string;
  headingHighlight: string;
  subtitle?: string;
  cardTitle: string;
  cardSubtitle: string;
  emailLabel: string;
  emailHint?: string;
  submit: string;
}

const COPY_BY_MODE: Record<'normal' | 'issueReport', SignupCopy> = {
  normal: {
    badge: UI_STRINGS.SIGNUP.BADGE,
    headingPrefix: UI_STRINGS.SIGNUP.HEADING_PREFIX,
    headingHighlight: UI_STRINGS.SIGNUP.HEADING_HIGHLIGHT,
    subtitle: UI_STRINGS.SIGNUP.SUBTITLE,
    cardTitle: UI_STRINGS.SIGNUP.CARD_TITLE,
    cardSubtitle: UI_STRINGS.SIGNUP.CARD_SUBTITLE,
    emailLabel: UI_STRINGS.SIGNUP.EMAIL_LABEL,
    submit: UI_STRINGS.SIGNUP.SUBMIT,
  },
  issueReport: {
    headingPrefix: UI_STRINGS.SIGNUP.ISSUE_REPORT_HEADING_PREFIX,
    headingHighlight: UI_STRINGS.SIGNUP.ISSUE_REPORT_HEADING_HIGHLIGHT,
    cardTitle: UI_STRINGS.SIGNUP.ISSUE_REPORT_CARD_TITLE,
    cardSubtitle: UI_STRINGS.SIGNUP.ISSUE_REPORT_CARD_SUBTITLE,
    emailLabel: UI_STRINGS.SIGNUP.ISSUE_REPORT_EMAIL_LABEL,
    emailHint: UI_STRINGS.SIGNUP.ISSUE_REPORT_EMAIL_HINT,
    submit: UI_STRINGS.SIGNUP.ISSUE_REPORT_SUBMIT,
  },
};

const ICON_SIZE = 16;

const SIGNUP_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.EMAIL_ALREADY_REGISTERED]: UI_STRINGS.SIGNUP.ERROR_EMAIL_EXISTS,
  [API_ERROR_CODES.EMAIL_UNAVAILABLE]: UI_STRINGS.SIGNUP.ERROR_EMAIL_UNAVAILABLE,
  [API_ERROR_CODES.ISSUE_REPORT_COMPANY_EMAIL_REQUIRED]: UI_STRINGS.SIGNUP.ERROR_ISSUE_REPORT_COMPANY_EMAIL,
  [API_ERROR_CODES.SIGNUP_COMPANY_DETAILS_REQUIRED]: UI_STRINGS.SIGNUP.ERROR_COMPANY_DETAILS,
};

export function SignupPage() {
  const { mutate, isPending, isError, error } = useSignup();
  const [termsOpen, setTermsOpen] = useState(false);
  // Only the URL names the intent (FR-219), so a stored intent from an
  // abandoned journey never makes a normal signup subject to the company-email
  // rule (FR-207) or the company fields (FR-218).
  const intent = usePurchaseIntent(JOURNEY_ROUTE.REGISTER);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormValues>({ resolver: zodResolver(signupSchemaFor(intent !== null)) });

  const onSubmit = ({ fullName, email, password, tosAccepted, companyName, companyWebsite }: SignupFormValues) =>
    mutate({
      fullName,
      email,
      password,
      tosAccepted,
      ...(intent && { intent, companyName, companyWebsite: websiteSchema.parse(companyWebsite) }),
    });

  const copy = COPY_BY_MODE[intent ? 'issueReport' : 'normal'];

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      {intent && <IssueReportJourneySteps stage="account" route={JOURNEY_ROUTE.REGISTER} className="mb-10" />}
      <AuthHero
        badgeIcon={copy.badge ? <Star size={14} fill="currentColor" /> : undefined}
        badgeText={copy.badge}
        heading={
          <>
            {copy.headingPrefix}
            <span className="text-green">{copy.headingHighlight}</span>
          </>
        }
        subtitle={copy.subtitle}
      />

      {/* Only on the Issue Report journey, under the heading: a normal signup page is unchanged. */}
      {intent && (
        <p className="mt-4 text-sm text-slate">
          {UI_STRINGS.SIGNUP.HAVE_ACCOUNT}{' '}
          <Link
            to={withPurchaseIntent(ROUTES.AUTH.LOGIN, intent)}
            className="text-green font-semibold transition-colors hover:text-dkgreen"
          >
            {UI_STRINGS.SIGNUP.LOG_IN}
          </Link>
        </p>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="mt-10 w-full max-w-3xl">
        <SectionCard
          title={copy.cardTitle}
          subtitle={copy.cardSubtitle}
          icon={<UserRound size={20} />}
        >
          {isError && (
            <p role="alert" className="mb-6 text-center text-sm text-danger">
              {mapApiError(error, SIGNUP_ERROR_BY_CODE, UI_STRINGS.SIGNUP.ERROR)}
            </p>
          )}

          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <TextField
              {...register('fullName')}
              label={UI_STRINGS.SIGNUP.FULL_NAME_LABEL}
              required
              type="text"
              autoComplete="name"
              placeholder={UI_STRINGS.SIGNUP.FULL_NAME_PLACEHOLDER}
              icon={<UserRound size={ICON_SIZE} />}
              error={errors.fullName?.message}
            />
            <TextField
              {...register('email')}
              label={copy.emailLabel}
              helpText={copy.emailHint}
              required
              type="email"
              autoComplete="email"
              placeholder={UI_STRINGS.SIGNUP.EMAIL_PLACEHOLDER}
              icon={<Mail size={ICON_SIZE} />}
              error={errors.email?.message}
            />
            <TextField
              {...register('password')}
              label={UI_STRINGS.SIGNUP.PASSWORD_LABEL}
              required
              type="password"
              autoComplete="new-password"
              placeholder={UI_STRINGS.SIGNUP.PASSWORD_PLACEHOLDER}
              icon={<Lock size={ICON_SIZE} />}
              error={errors.password?.message}
            />
            <TextField
              {...register('confirmPassword')}
              label={UI_STRINGS.SIGNUP.CONFIRM_PASSWORD_LABEL}
              required
              type="password"
              autoComplete="new-password"
              placeholder={UI_STRINGS.SIGNUP.PASSWORD_PLACEHOLDER}
              icon={<Lock size={ICON_SIZE} />}
              error={errors.confirmPassword?.message}
            />
            <TextField
              {...register('companyName')}
              label={UI_STRINGS.SIGNUP.COMPANY_NAME_LABEL}
              optional={!intent}
              required={Boolean(intent)}
              type="text"
              autoComplete="organization"
              placeholder={UI_STRINGS.SIGNUP.COMPANY_NAME_PLACEHOLDER}
              icon={<Building2 size={ICON_SIZE} />}
              error={errors.companyName?.message}
            />
            {intent && (
              <TextField
                {...register('companyWebsite')}
                label={UI_STRINGS.SIGNUP.COMPANY_WEBSITE_LABEL}
                required
                type="text"
                inputMode="url"
                autoComplete="url"
                placeholder={UI_STRINGS.SIGNUP.COMPANY_WEBSITE_PLACEHOLDER}
                helpText={UI_STRINGS.SIGNUP.COMPANY_WEBSITE_HINT}
                icon={<Globe size={ICON_SIZE} />}
                error={errors.companyWebsite?.message}
              />
            )}
          </div>
        </SectionCard>

        <div className="mt-6 flex flex-col items-center gap-5">
          <CheckboxField
            {...register('tosAccepted')}
            label={
              <>
                {UI_STRINGS.SIGNUP.CONSENT_LABEL_PREFIX}
                <Button
                  type="button"
                  variant="link"
                  onClick={(event) => {
                    // Nested inside the checkbox's <label> - without this the
                    // native label-forwards-click-to-control behaviour would
                    // also toggle the checkbox when this link is clicked.
                    event.stopPropagation();
                    setTermsOpen(true);
                  }}
                  className="underline"
                >
                  {UI_STRINGS.SIGNUP.CONSENT_LABEL_LINK}
                </Button>
                {UI_STRINGS.SIGNUP.CONSENT_LABEL_SUFFIX}
              </>
            }
            error={errors.tosAccepted ? UI_STRINGS.SIGNUP.CONSENT_REQUIRED_ERROR : undefined}
          />
          <ShieldNote>{UI_STRINGS.SIGNUP.ENCRYPTION_NOTE}</ShieldNote>
          <Button
            type="submit"
            variant="navy"
            disabled={isPending}
            className="inline-flex items-center gap-2 px-10 py-3.5 text-base drop-shadow-md"
          >
            {isPending ? UI_STRINGS.SIGNUP.SUBMITTING : copy.submit}
            <ArrowRight size={18} aria-hidden="true" />
          </Button>
        </div>
      </form>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </div>
  );
}
